import React, { useState, useMemo } from 'react';
import Editor from '@monaco-editor/react';
import Ajv from 'ajv';

export const SchemaValidatorView: React.FC = () => {
  const [schema, setSchema] = useState<string>('{\n  "type": "object",\n  "properties": {\n    "name": { "type": "string" }\n  },\n  "required": ["name"]\n}');
  const [json, setJson] = useState<string>('{\n  "name": "MamaJSON"\n}');

  const result = useMemo(() => {
    try {
      if (!schema.trim() || !json.trim()) return { valid: true };
      const parsedSchema = JSON.parse(schema) as object;
      const parsedJson = JSON.parse(json) as unknown;
      
      const ajv = new Ajv({ allErrors: true });
      const validate = ajv.compile(parsedSchema);
      const valid = validate(parsedJson);
      
      if (valid) {
        return { valid: true };
      } else {
        return { valid: false, errors: validate.errors };
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        return { valid: false, errors: [{ message: err.message }] };
      } else {
        return { valid: false, errors: [{ message: String(err) }] };
      }
    }
  }, [schema, json]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, padding: '1rem' }}>
      <div style={{ marginBottom: '1rem' }}>
        <h2 style={{ margin: 0 }}>JSON Schema Validation</h2>
        <p style={{ margin: '0.25rem 0 0', color: 'var(--color-text-secondary)' }}>Validate JSON objects against a JSON Schema.</p>
      </div>
      
      <div style={{ display: 'flex', gap: '1rem', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ fontSize: '0.9rem', marginBottom: '0.5rem', color: 'var(--color-text-secondary)' }}>JSON Schema</h3>
          <div style={{ flex: 1, minHeight: 0, position: 'relative', border: '1px solid var(--color-border)', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
            <Editor
              height="100%"
              defaultLanguage="json"
              value={schema}
              onChange={(value) => { setSchema(value ?? ''); }}
              theme="vs-dark"
              options={{ minimap: { enabled: false }, padding: { top: 16 } }}
            />
          </div>
        </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ fontSize: '0.9rem', marginBottom: '0.5rem', color: 'var(--color-text-secondary)' }}>JSON Data</h3>
          <div style={{ flex: 1, minHeight: 0, position: 'relative', border: '1px solid var(--color-border)', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
            <Editor
              height="100%"
              defaultLanguage="json"
              value={json}
              onChange={(value) => { setJson(value ?? ''); }}
              theme="vs-dark"
              options={{ minimap: { enabled: false }, padding: { top: 16 } }}
            />
          </div>
        </div>
        </div>
      </div>
      
      <div style={{ marginTop: '1rem', padding: '1rem', borderRadius: '8px', background: result.valid ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)', border: `1px solid ${result.valid ? '#4CAF50' : '#F44336'}` }}>
        <h3 style={{ margin: '0 0 0.5rem 0', color: result.valid ? '#4CAF50' : '#F44336' }}>
          {result.valid ? '✅ Valid JSON' : '❌ Validation Errors'}
        </h3>
        {!result.valid && result.errors && (
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', color: 'var(--color-text-primary)' }}>
            {JSON.stringify(result.errors, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
};
